import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { agentFieldValuesPath, useApi } from "../api/client";
import type { AgentFieldValuesResponse } from "../api/types";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { Pagination } from "./Pagination";
import { Bar, Empty, ErrorState, Loading } from "./States";
import { SegmentedControl, Toolbar } from "./Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { EM_DASH, fmtDateTime, fmtTokens, fmtValue, humanise } from "../lib/format";

type Fill = "all" | "populated" | "empty";

const FILLS: { key: Fill; label: string }[] = [
    { key: "all", label: "All" },
    { key: "populated", label: "Populated" },
    { key: "empty", label: "Blank" },
];

const SORTS = [
    { key: "recent", label: "Newest first" },
    { key: "oldest", label: "Oldest first" },
    { key: "conf_asc", label: "Lowest confidence first" },
    { key: "conf_desc", label: "Highest confidence first" },
];

/**
 * Every value one agent has read for one field.
 *
 * The Fields table says whether the agent can read a field. This says what it
 * read — and that is the step where a problem becomes visible. A field with a
 * 92% average and a 100% fill rate looks healthy right up until you see that
 * all twenty runs returned the same company name, or that the "populated"
 * value is the word "Unknown".
 *
 * So every row links to the document it came from. Confidence is the model
 * marking its own work; the original scan is the only thing that settles
 * whether a value is right, and it is one click away on each row.
 */
export function AgentFieldValues({
    documentType,
    field,
    onBack,
}: {
    documentType: string;
    field: string;
    onBack: () => void;
}) {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();

    const page = Number(params.get("fv_page") ?? 1);
    const sort = params.get("fv_sort") ?? "recent";
    const fill = (params.get("fv_fill") ?? "all") as Fill;
    const [q, setQ] = useState(params.get("fv_q") ?? "");

    const setParam = (key: string, value: string | null) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        // Any change to the filters invalidates the page number: staying on
        // page 4 of a narrower result set shows an empty table.
        if (key !== "fv_page") next.set("fv_page", "1");
        setParams(next);
    };

    useEffect(() => {
        const t = setTimeout(() => {
            if ((params.get("fv_q") ?? "") !== q) setParam("fv_q", q || null);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const { data, error, loading, reload } = useApi<AgentFieldValuesResponse>(
        agentFieldValuesPath(documentType, field, {
            page,
            page_size: 15,
            sort,
            fill,
            q: params.get("fv_q") || undefined,
        })
    );

    const exportCsv = () => {
        if (!data?.items.length) return;
        downloadCsv(
            stampedName(`field-${documentType}-${field}`),
            toCsv(data.items, [
                { header: "Extracted at", value: (r) => r.created_at },
                { header: "Value", value: (r) => r.value_text },
                { header: "Field confidence", value: (r) => r.confidence },
                { header: "Document confidence", value: (r) => r.run_confidence },
                { header: "Document", value: (r) => r.original_filename },
                { header: "Document ID", value: (r) => r.document_id },
                { header: "Run status", value: (r) => r.status },
            ])
        );
    };

    const s = data?.stats;
    const repeated = data?.distinct_values.filter((d) => d.runs > 1) ?? [];

    return (
        <>
            <Toolbar
                title={`${humanise(field)}`}
                subtitle={
                    data
                        ? `${fmtTokens(data.stats.runs)} extraction${
                              data.stats.runs === 1 ? "" : "s"
                          } by the ${data.label} agent · ${fmtTokens(
                              data.stats.distinct_values
                          )} distinct value${data.stats.distinct_values === 1 ? "" : "s"}`
                        : "Every value this agent has read for this field"
                }
                onExport={exportCsv}
                exportDisabled={!data?.items.length}
                search={q}
                onSearch={setQ}
                searchPlaceholder="Search values"
                right={
                    <>
                        <button onClick={onBack}>← All fields</button>
                        <SegmentedControl
                            segments={FILLS}
                            value={fill}
                            onChange={(v) => setParam("fv_fill", v === "all" ? null : v)}
                            label="Show"
                        />
                        <select
                            value={sort}
                            aria-label="Sort values"
                            onChange={(e) =>
                                setParam("fv_sort", e.target.value === "recent" ? null : e.target.value)
                            }
                        >
                            {SORTS.map((o) => (
                                <option key={o.key} value={o.key}>{o.label}</option>
                            ))}
                        </select>
                    </>
                }
            />

            {s ? (
                <div className="tiles">
                    <div className="tile">
                        <div className="tile-label">Fill rate</div>
                        <div className="tile-value">
                            {s.fill_rate === null ? EM_DASH : `${Math.round(s.fill_rate * 100)}%`}
                        </div>
                        <div className={`tile-note${s.populated < s.runs ? " warn" : ""}`}>
                            {fmtTokens(s.populated)} of {fmtTokens(s.runs)} populated
                        </div>
                    </div>
                    <div className="tile">
                        <div className="tile-label">Avg confidence</div>
                        <div className="tile-value">
                            {s.avg_confidence === null ? EM_DASH : `${s.avg_confidence.toFixed(1)}%`}
                        </div>
                        <div className="tile-note">
                            {s.scored < s.runs
                                ? `only ${fmtTokens(s.scored)} of ${fmtTokens(s.runs)} scored`
                                : "self-reported, not accuracy"}
                        </div>
                    </div>
                    <div className="tile">
                        <div className="tile-label">Confidence range</div>
                        <div className="tile-value" style={{ fontSize: 20 }}>
                            {s.min_confidence === null || s.max_confidence === null
                                ? EM_DASH
                                : `${s.min_confidence}–${s.max_confidence}%`}
                        </div>
                        <div className="tile-note">lowest to highest single reading</div>
                    </div>
                    <div className="tile">
                        <div className="tile-label">Distinct values</div>
                        <div className="tile-value">{fmtTokens(s.distinct_values)}</div>
                        <div className="tile-note">
                            {s.distinct_values === 1 && s.populated > 1
                                ? "the same value on every run"
                                : "across all populated runs"}
                        </div>
                    </div>
                </div>
            ) : null}

            {repeated.length ? (
                <div className="card">
                    <div className="card-head bordered">
                        <span className="card-title">Values seen more than once</span>
                        <span className="muted">most frequent first</span>
                    </div>
                    <div className="card-body">
                        {/* Worth its own block: a placeholder the model falls back
                            to ("Unknown", "N/A", the same name on every document)
                            is invisible in an average and unmistakable here. */}
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th scope="col">Value</th>
                                        <th scope="col" className="num">Runs</th>
                                        <th scope="col">Share</th>
                                        <th scope="col">Avg confidence</th>
                                        <th scope="col">Last seen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {repeated.map((d) => (
                                        <tr key={d.val_text}>
                                            <td className="value-cell">{d.val_text}</td>
                                            <td className="num">{fmtTokens(d.runs)}</td>
                                            <td style={{ width: 140 }}>
                                                <Bar value={d.runs} max={s?.populated ?? d.runs} />
                                            </td>
                                            <td>
                                                <ConfidenceBadge
                                                    value={d.avg_confidence}
                                                    absentReason="No run of this value carried a confidence score"
                                                />
                                            </td>
                                            <td className="nowrap muted">{fmtDateTime(d.last_seen)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="card">
                {loading ? (
                    <Loading what="field values" />
                ) : error ? (
                    <ErrorState message={error} onRetry={reload} />
                ) : !data || data.items.length === 0 ? (
                    <Empty
                        title={
                            q || fill !== "all"
                                ? "No extraction matches this filter"
                                : "This agent has never extracted this field"
                        }
                        hint={
                            q || fill !== "all"
                                ? "Clear the search or switch back to All."
                                : "The field appears in no stored extraction for this document type."
                        }
                    />
                ) : (
                    <>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th scope="col" className="si">Sl</th>
                                        <th scope="col">Value</th>
                                        <th scope="col">Field confidence</th>
                                        <th scope="col">Document</th>
                                        <th scope="col">Extracted</th>
                                        <th scope="col">Cross-check</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((r, i) => (
                                        <tr
                                            key={r.run_id}
                                            className={r.document_id ? "row-link" : undefined}
                                            onClick={
                                                r.document_id
                                                    ? () =>
                                                          navigate(
                                                              `/documents/${r.document_id}` +
                                                                  `?tab=fields&field=${encodeURIComponent(field)}`
                                                          )
                                                    : undefined
                                            }
                                        >
                                            <td className="si">
                                                {(data.page - 1) * data.page_size + i + 1}
                                            </td>
                                            <td className="value-cell">
                                                {r.populated ? (
                                                    fmtValue(r.value)
                                                ) : (
                                                    <span className="muted">
                                                        {EM_DASH} not read
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <ConfidenceBadge
                                                    value={r.confidence}
                                                    absentReason="This extraction stored no confidence for this field"
                                                />
                                                {/* The document's own score, kept
                                                    visibly separate: they disagree
                                                    often and conflating them hides
                                                    a weak field in a strong run. */}
                                                {r.run_confidence !== null ? (
                                                    <div className="muted" style={{ fontSize: 12 }}>
                                                        document {r.run_confidence.toFixed(0)}%
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td>
                                                {r.original_filename ?? (
                                                    <span className="muted">unnamed file</span>
                                                )}
                                                {!r.document_active ? (
                                                    <>
                                                        {" "}
                                                        <span
                                                            className="pill pill-warning"
                                                            title="The document was withdrawn after this extraction ran, so the original file may not open."
                                                        >
                                                            withdrawn
                                                        </span>
                                                    </>
                                                ) : null}
                                                {r.status && r.status !== "completed" ? (
                                                    <div className="muted" style={{ fontSize: 12 }}>
                                                        run {humanise(r.status)}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="nowrap muted">
                                                {fmtDateTime(r.created_at)}
                                            </td>
                                            <td className="nowrap">
                                                {r.document_id ? (
                                                    <Link
                                                        to={`/documents/${r.document_id}?tab=document`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Original document
                                                    </Link>
                                                ) : (
                                                    <span className="muted">{EM_DASH}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            page={data.page}
                            totalPages={data.total_pages}
                            total={data.total}
                            pageSize={data.page_size}
                            noun="extractions"
                            onPage={(p) => setParam("fv_page", String(p))}
                        />
                    </>
                )}
            </div>

            <div className="notice" style={{ marginBottom: 0 }}>
                Click any row to open that document with this field highlighted, or{" "}
                <strong>Original document</strong> to go straight to the scan. Confidence here is
                the model scoring its own reading — only the page itself settles whether the value
                is right, which is why every row carries a link to it.
            </div>
        </>
    );
}
